/**
 * The error codes the verification surface can answer with (U3).
 *
 * They were api-local string literals while only the api read them. P15b is
 * where that stops being survivable: the staff review screen has to say
 * something different for each of them, because they lead to genuinely different
 * next actions — and a client that re-typed the strings would fall through to
 * "something went wrong" the day one of them is renamed, on the one screen where
 * "somebody else already decided this case" and "you may not review this member"
 * must never read the same.
 *
 * Same shape, same argument, as `MEDIA_ERROR_CODES` (P15a) and
 * `ACCESS_ERROR_CODES` (P10): one place a code is spelled, and the api's
 * `DomainError` subclasses read it from here.
 */
export const VERIFICATION_ERROR_CODES = {
  /** No case at all — a member who never reached verification, or a bad id. */
  caseNotFound: 'verification_case_not_found',
  /**
   * The move is not one the machine allows from where the case stands.
   *
   * 422, and the reviewer's answer is to re-read the case: the buttons are
   * derived from `VERIFICATION_CASE_TRANSITIONS`, so seeing this means the
   * screen is looking at a state the server has already left.
   */
  illegalCaseTransition: 'verification_illegal_case_transition',
  /**
   * A colleague moved the case between the decision and the write (N27).
   *
   * 409, and deliberately not the same as {@link illegalCaseTransition}: the
   * move *was* legal when it was chosen. The client refetches and says so — it
   * never retries, because re-deciding silently is how two reviewers both
   * believe they approved a case.
   */
  caseChanged: 'verification_case_changed',
  /** The case has no such step — usually the member's gender waived it (U15). */
  stepNotInCase: 'verification_step_not_in_case',
  /** `face_match` is computed and `interview` is booked; neither is submitted. */
  stepNotSubmittable: 'verification_step_not_submittable',
  /** The case is in the queue or under review — not the member's to change. */
  caseNotEditable: 'verification_case_not_editable',
  /** A reviewer already passed or waived this step; it is closed until re-opened. */
  stepAlreadyResolved: 'verification_step_already_resolved',
  /** "Accept evidence" found no submitted evidence step to accept (F7). 422. */
  nothingToAccept: 'verification_nothing_to_accept',
  /** The named upload is not a finalized asset of the right kind for this member. */
  evidenceNotAcceptable: 'verification_evidence_not_acceptable',
  /** The member's own asset exists, but the upload never finished. */
  evidenceNotReady: 'verification_evidence_not_ready',
  /** Steps are still outstanding, so the case cannot be submitted yet. */
  caseNotReady: 'verification_case_not_ready',
  /** Staff acted on an interview the case does not have. */
  interviewNotFound: 'verification_interview_not_found',
  /**
   * The ID and the selfie have not yet been accepted by a reviewer, and
   * `verification.interview.unlockAfterEvidence` holds the interview until they
   * are (F4). 422: the member's screen re-reads the case and draws the lock.
   */
  interviewEvidenceNotApproved: 'verification_interview_evidence_not_approved',
  /** The fee is switched on for this member and no unconsumed ticket is held — pay first (D9a). */
  interviewFeeRequired: 'verification_interview_fee_required',
  /** The slot is closed, full, begun, too soon, withdrawn, or offered to another audience. */
  interviewSlotUnavailable: 'verification_interview_slot_unavailable',
  /** The current appointment is inside `verification.interviewRescheduleCutoffMinutes`. */
  interviewRescheduleCutoff: 'verification_interview_reschedule_cutoff',
  /** Staff tried to withdraw, re-time or shrink a slot that live interviews still name. */
  interviewSlotInUse: 'verification_interview_slot_in_use',
  /** Staff tried to publish a window over one that already exists for the same audience and method. */
  interviewSlotOverlap: 'verification_interview_slot_overlap',
  /** The window is inverted, shorter than the floor, or beyond the horizon. */
  interviewSlotWindowInvalid: 'verification_interview_slot_window_invalid',
  /** No such published slot. */
  interviewSlotNotFound: 'verification_interview_slot_not_found',
  /** A patch would leave a video window without its meeting link. */
  interviewSlotLinkRequired: 'verification_interview_slot_link_required',
  /**
   * `verification.genderMatchedReview` refuses this reviewer this member.
   *
   * 403 and state-independent — an authority refusal, not a state one, which is
   * why the screen renders read-only rather than offering a retry.
   */
  genderMismatch: 'verification_gender_mismatch',
  /**
   * A reviewer has the member's re-review open (D6). 409: the edit is refused
   * and the stored answer is left as it was, so the reviewer judges what they
   * are looking at; the member tries again once it is decided.
   */
  reReviewUnderReview: 'verification_rereview_under_review',
  /**
   * The member's last re-review was decided an instant ago and their status has
   * not yet followed (D6). 409, transient: a refetch of `/me` resolves it.
   */
  reReviewDeciding: 'verification_rereview_deciding',
  /** The reason names evidence a case of this kind never collected (D6). 422. */
  decisionReasonNotApplicable: 'verification_decision_reason_not_applicable',
} as const;

export type VerificationErrorCode =
  (typeof VERIFICATION_ERROR_CODES)[keyof typeof VERIFICATION_ERROR_CODES];
